<template>
  <div class="chat-container">
    <!-- 聊天主容器 -->
    <div ref="messageContainer" class="chat-messages">
      <!-- 消息列表 -->
      <div v-for="(message, index) in messages" :key="index" class="message" :class="message.type">
        <div class="message-content">
          <div class="message-header">
            <span class="user-name">{{ message.userName }}</span>
            <!-- TODO: 优化按钮间距，使复制和删除按钮紧贴 -->
            <span class="message-actions">
              <el-tooltip :content="$t('CargoMsg.Cargo_Copy')" placement="top">
                <el-button type="text" icon="el-icon-document-copy" size="mini" class="message-action-btn" @click="copyMessage(message.text)" />
              </el-tooltip>
              <el-tooltip :content="$t('CargoMsg.Cargo_Delete')" placement="top">
                <el-button type="text" icon="el-icon-delete" size="mini" class="message-action-btn" @click="deleteMessage(index)" />
              </el-tooltip>
            </span>
          </div>
          <div v-if="!message.isHtml" class="message-text" v-text="message.text" />
          <div v-else class="message-text" v-html="message.text" />
        </div>
      </div>
    </div>
    <!-- 加载中指示 -->
    <div v-if="isLoading" class="loading-indicator">
      <div class="loading-circle" />
      <span class="loading-text">{{ $t('CargoMsg.Cargo_ChatThinking') }}</span>
    </div>
    <!-- 录音状态指示器 -->
    <div v-if="voiceRecognition.isRecording" class="loading-indicator voice-recording-indicator">
      <i class="el-icon-microphone voice-icon" />
      <span class="loading-text">{{ $t('CargoMsg.Cargo_VoiceRecording', [voiceRecognition.remainingTime]) }}</span>
      <div class="voice-wave">
        <span class="wave-bar" />
        <span class="wave-bar" />
        <span class="wave-bar" />
      </div>
    </div>
    <!-- 输入区 -->
    <div class="chat-input">
      <div class="input-container">
        <el-input
          v-model="inputMessage"
          type="textarea"
          :rows="5"
          :placeholder="voiceRecognition.isRecording ? $t('CargoMsg.Cargo_VoiceRecordingInput') : $t('CargoMsg.Cargo_ChatInputPlaceholder')"
          :disabled="isLoading || isRevision"
          class="message-input"
          :class="{ 'voice-recording': voiceRecognition.isRecording }"
          @keydown.enter.native="handleEnter"
          @input="handleManualInput"
        />

        <div class="button-group">
          <el-button
            type="primary"
            icon="el-icon-s-promotion"
            :loading="isLoading"
            :disabled="voiceButtonDisabled"
            class="send-btn"
            @click="sendMessage"
          >
            {{ $t('CargoMsg.Cargo_Send') }}
          </el-button>
          <el-button
            :type="voiceRecognition.isRecording ? 'danger' : 'default'"
            :icon="voiceButtonIcon"
            :loading="voiceRecognition.isProcessing"
            :disabled="voiceButtonDisabled"
            class="voice-btn"
            :class="{ 'recording': voiceRecognition.isRecording }"
            @click="handlePlaceholderAction"
          >
            {{ voiceButtonText }}
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { analyzerDetectFunction } from '@/api/sysManage/aiCustomPage'
import { PAGE_PART_TYPE } from '@/class/cargo/model/parameters/AiCustomPageConstants'
import { functionRegistry } from '@/store/modules/aiCustomPage/function-registry'
import { asrUtils } from '@/utils/asrUtils'

export default {
  name: 'AiCustomPageChatView',
  props: {
    pageParts: {
      type: Array,
      required: true,
      default: () => []
    },
    isRevision: {
      type: Boolean,
      default: false
    },
    formUuid: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      messages: [], // 聊天消息数组
      inputMessage: '', // 输入框内容
      isLoading: false, // 是否正在加载
      loadingKey: 0, // 用于强制重新渲染loading指示器
      sessionId: this.getSessionId(), // 会话ID

      // 语音识别相关
      voiceRecognition: {
        isRecording: false,
        isProcessing: false,
        currentText: '',
        finalText: '',
        error: null,
        sessionId: null,
        originalInputText: '', // 保存录音开始前的原始输入内容
        accumulatedText: '', // 累积的识别文本
        textMerger: null, // 文本合并器实例
        userInputText: '', // 用户真正手动输入的内容（不包括语音识别内容）
        remainingTime: 60, // 剩余录音时间（秒）
        timeCounter: null // 时间计数器
      },

      // 语音识别管理器实例
      voiceManager: null
    }
  },
  computed: {
    // 元素映射表，以elementName为key
    elementMap() {
      const map = {}
      for (const pagePart of this.pageParts) {
        for (const element of pagePart.elements) {
          map[element.elementName] = element
        }
      }
      return map
    },

    // 语音按钮文本
    voiceButtonText() {
      if (this.voiceRecognition.isRecording) {
        return this.voiceRecognition.isProcessing ? this.$t('CargoMsg.Cargo_VoiceRecognizing') : this.$t('CargoMsg.Cargo_VoiceStopRecording')
      }
      return this.$t('CargoMsg.Cargo_VoiceStartRecording')
    },

    // 语音按钮图标
    voiceButtonIcon() {
      if (this.voiceRecognition.isRecording) {
        return this.voiceRecognition.isProcessing ? 'el-icon-loading' : 'el-icon-video-pause'
      }
      return 'el-icon-microphone'
    },

    // 语音按钮禁用状态
    voiceButtonDisabled() {
      return this.isLoading || this.isRevision || this.voiceRecognition.isProcessing
    }
  },
  watch: {
    messages: {
      handler(newMessages) {
        sessionStorage.setItem(`aiCustomPageChatMessages_${this.sessionId}_${this.formUuid}`, JSON.stringify(newMessages))
      },
      deep: true
    }
  },
  created() {
    // 从sessionStorage加载消息
    const savedMessages = sessionStorage.getItem(`aiCustomPageChatMessages_${this.sessionId}_${this.formUuid}`)
    if (savedMessages) {
      this.messages = JSON.parse(savedMessages)
    }

    // 初始化用户输入文本
    this.voiceRecognition.userInputText = this.inputMessage || ''

    // 初始化语音识别
    this.initVoiceRecognition()
  },
  mounted() {
    this.scrollToBottom()
  },

  beforeDestroy() {
    // 清理语音识别资源
    this.stopTimeCounter()
    if (this.voiceManager) {
      try {
        this.voiceManager.cleanup()
      } catch (error) {
        console.error('[语音识别] 组件销毁时清理失败:', error)
      }
    }
  },
  methods: {
    getSessionId() {
      return window.sessionStorage.key(0)?.split('_')[0] || 'default'
    },
    // 过滤pageParts数据，只保留对LLM相对有用的字段（主要去掉无可读性的字段）
    // 只保留业务字段的分块
    filterPagePartsForLLM(pageParts) {
      return pageParts
        .filter(pagePart =>
          pagePart.blockType === PAGE_PART_TYPE.BUSINESS_ELEMENTS.value
        )
        .map(pagePart => ({
          serialNumber: pagePart.serialNumber,
          blockType: pagePart.blockType,
          elements: pagePart.elements.map(element => ({
            isDefaultRequire: element.isDefaultRequire,
            isReadonly: element.isReadonly,
            isRequire: element.isRequire,
            serialNumber: element.serialNumber,
            elementName: element.elementName,
            languageList: element.languageList.map(lang => ({
              name: lang.name,
              language: lang.language,
              type: lang.type
            }))
          })),
          name: pagePart.name,
          languageList: pagePart.languageList.map(lang => ({
            name: lang.name,
            language: lang.language,
            type: lang.type
          })),
          id: pagePart.id
        }))
    },
    // 处理 Ctrl+Enter 发送消息
    handleEnter(e) {
      if (e.ctrlKey) {
        e.preventDefault()
        this.sendMessage()
      }
    },

    // 处理用户手动输入
    handleManualInput() {
      // 只有在不录音时，才更新用户输入文本
      if (!this.voiceRecognition.isRecording) {
        this.voiceRecognition.userInputText = this.inputMessage
      }
    },
    /**
     * 核心：通用规则处理方法
     * @param {Array} rules - 从API获取的规则数组
     * @param {string} type - 规则类型 ('validation', 'sync', 'visibility')
     *  @returns {Promise<Object>} 返回一个包含处理数量和UI信息的对象
     */
    async processRuleType(rules, type) {
      if (!rules || rules.length === 0) {
        return { count: 0, info: '' }
      }
      const addedFunctions = []
      await Promise.all(rules.map(async func => {
        if (func.id) {
          try {
            // 1. 数据格式化
            let formattedFunction
            switch (type) {
              case 'validation':
                formattedFunction = { id: func.id, type, code: func.code, metadata: { elements: func.metadata.elements, errorMessage: func.metadata.error_message, sourceQuery: func.metadata.source_query }}
                break
              case 'sync':
                formattedFunction = { id: func.id, type, code: func.code, metadata: { elements: func.metadata.elements, trigger: func.metadata.trigger, sourceQuery: func.metadata.source_query }}
                break
              case 'visibility':
                formattedFunction = { id: func.id, type, code: func.code, metadata: { elements: func.metadata.elements, targetElement: func.metadata.target_element, sourceQuery: func.metadata.source_query }}
                break
            }

            // 2. 校验和注册
            if (this.validateFormattedFunction(formattedFunction)) {
              functionRegistry.registerFunction(this.formUuid, func.id, func.code, type, formattedFunction.metadata)
              addedFunctions.push(formattedFunction)
            }
          } catch (error) {
            console.error(`[AI聊天] 添加 ${type} 函数失败:`, error)
          }
        }
      }))

      // 3. 生成UI反馈信息
      const info = addedFunctions.map(func => {
        const escapedCode = func.code.replace(/`/g, '\\`')
        let details = ''
        switch (type) {
          case 'validation': {
            const displayErrorMessage = Array.isArray(func.metadata.errorMessage) ? (func.metadata.errorMessage.find(msg => msg.language === 'zh_CN')?.name || func.metadata.errorMessage[0]?.name || '') : func.metadata.errorMessage || ''
            details = `${this.$t('CargoMsg.Cargo_ValidationRule')}: ${displayErrorMessage}\n${this.$t('CargoMsg.Cargo_RelatedElements')}: ${func.metadata.elements.join(', ')}`
            break
          }
          case 'sync':
            details = `${this.$t('CargoMsg.Cargo_TriggerElement')}: ${func.metadata.trigger.element}\n${this.$t('CargoMsg.Cargo_RelatedElements')}: ${func.metadata.elements.join(', ')}`
            break
          case 'visibility':
            details = `${this.$t('CargoMsg.Cargo_TargetElement')}: ${func.metadata.targetElement}\n${this.$t('CargoMsg.Cargo_RelatedElements')}: ${func.metadata.elements.join(', ')}`
            break
        }
        return `<div class="function-block"><div class="function-info">${this.$t('CargoMsg.Cargo_RuleID')}: ${func.id}\n${details}\n${this.$t('CargoMsg.Cargo_TriggerCondition')}: ${func.metadata.sourceQuery}\n${this.$t('CargoMsg.Cargo_FunctionCode')}:\n\`\`\`javascript\n${escapedCode}\n\`\`\`</div></div>`
      }).join('\n\n')

      return { count: addedFunctions.length, info }
    },
    // 发送消息逻辑
    async sendMessage() {
      if (!this.inputMessage.trim() || this.isLoading) return
      const userMessage = this.inputMessage
      this.inputMessage = ''

      // 添加用户消息
      this.messages.push({
        type: 'user',
        userName: this.$t('CargoMsg.Cargo_ChatUser'),
        text: userMessage,
        isHtml: false
      })

      // 添加AI消息占位符
      this.messages.push({
        type: 'ai',
        userName: this.$t('CargoMsg.Cargo_ChatAI'),
        text: '',
        isHtml: false
      })

      this.isLoading = true
      this.loadingKey++

      try {
        // 获取当前表单的所有规则函数
        const currentRules = functionRegistry.getFormFunctions(this.formUuid)
        // 调用函数检测API
        console.log('[AI聊天] 发送函数检测请求:', {
          page_parts: this.filterPagePartsForLLM(this.pageParts),
          query: userMessage,
          current_rules: currentRules
        })

        const response = await analyzerDetectFunction({
          page_parts: this.filterPagePartsForLLM(this.pageParts),
          query: userMessage,
          current_rules: currentRules
        })

        const responseData = response.data.data
        console.log(`[AI聊天] 函数检测响应:`, responseData)

        let summaryMessage = responseData.message || ''
        let hasRuleChanged = false
        this.$set(this.messages[this.messages.length - 1], 'isHtml', false) // 默认为纯文本

        // 处理复合操作响应
        const { intent_breakdown, results } = responseData
        const operationSummaries = []

        // 检查是否为空结果的情况
        if (results &&
            Array.isArray(results.ADD) && results.ADD.length === 0 &&
            Array.isArray(results.MODIFY) && results.MODIFY.length === 0 &&
            Array.isArray(results.DELETE) && results.DELETE.length === 0 &&
            Array.isArray(results.DELETE_UNEXIST) && results.DELETE_UNEXIST.length === 0) {
          summaryMessage = this.$t('CargoMsg.Cargo_UnknownIntent')
        } else {
        // 处理 ADD 操作
          if (intent_breakdown.ADD && intent_breakdown.ADD.length > 0 && results.ADD) {
            const addResult = await this.processAddOperations(results.ADD)
            if (addResult.count > 0) {
              operationSummaries.push(`${this.$t('CargoMsg.Cargo_AIAddRulesNumber', [addResult.count])}:\n\n${addResult.info}`)
              hasRuleChanged = true
            }
          }

          // 处理 MODIFY 操作
          if (intent_breakdown.MODIFY && intent_breakdown.MODIFY.length > 0 && results.MODIFY) {
            const modifyResult = await this.processModifyOperations(results.MODIFY)
            if (modifyResult.count > 0) {
              operationSummaries.push(`${this.$t('CargoMsg.Cargo_AIChangRulesNumber', [modifyResult.count])}:\n\n${modifyResult.info}`)
              hasRuleChanged = true
            }
          }

          // 处理 DELETE 操作
          if (intent_breakdown.DELETE && intent_breakdown.DELETE.length > 0 && results.DELETE) {
            const deleteResult = await this.processDeleteOperations(results.DELETE, currentRules)
            if (deleteResult.count > 0) {
              operationSummaries.push(`${this.$t('CargoMsg.Cargo_AIDeleteRulesNumber', [deleteResult.count])}:\n\n${deleteResult.info}`)
              hasRuleChanged = true
            }
          }

          // 处理 DELETE_UNEXIST 操作
          if (intent_breakdown.DELETE_UNEXIST && intent_breakdown.DELETE_UNEXIST.length > 0 && results.DELETE_UNEXIST) {
            const deleteUnexistResult = await this.processDeleteUnexistOperations(results.DELETE_UNEXIST)
            if (deleteUnexistResult.count > 0) {
              operationSummaries.push(`${this.$t('CargoMsg.Cargo_AIDeleteUnexistRulesNumber')}:\n\n${deleteUnexistResult.info}`)
            }
          }
        }
        // 组合最终消息
        if (operationSummaries.length > 0) {
          summaryMessage = summaryMessage + '\n\n' + operationSummaries.join('\n\n<hr>\n\n')
          this.$set(this.messages[this.messages.length - 1], 'isHtml', true)
        }

        // 更新聊天窗口
        console.log('已更新聊天窗口')
        this.$set(this.messages[this.messages.length - 1], 'text', summaryMessage)

        // 如果规则有变动，则触发全局UI刷新
        if (hasRuleChanged) {
          console.log('[AI聊天] 规则已变更，发出ruleChange事件')
          this.$emit('ruleChange')
        } else {
          console.log('[AI聊天] 规则无变更，不发出ruleChange事件')
        }
      } catch (error) {
        console.error('[AI聊天] API请求失败:', error)
        this.$set(this.messages[this.messages.length - 1], 'text', this.$t('CargoMsg.Cargo_ConnectionError'))
        this.$set(this.messages[this.messages.length - 1], 'isHtml', false)
      } finally {
        this.isLoading = false
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },
    // 滚动到底部
    scrollToBottom() {
      const container = this.$refs.messageContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },
    // 复制消息内容到剪贴板
    copyMessage(text) {
      if (navigator && navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message.success(this.$t('CargoMsg.Cargo_ChatCopySuccess'))
        }).catch(() => {
          this.$message.error(this.$t('CargoMsg.Cargo_ChatCopyFailed'))
        })
      } else {
        // 兼容旧浏览器
        const textarea = document.createElement('textarea')
        textarea.value = text
        document.body.appendChild(textarea)
        textarea.select()
        try {
          document.execCommand('copy')
          this.$message.success(this.$t('CargoMsg.Cargo_ChatCopySuccess'))
        } catch (err) {
          this.$message.error(this.$t('CargoMsg.Cargo_ChatCopyFailed'))
        }
        document.body.removeChild(textarea)
      }
    },
    // 删除指定消息
    deleteMessage(index) {
      this.$confirm(this.$t('CargoMsg.Cargo_ChatDeleteConfirm'), this.$t('CargoMsg.Cargo_ChatConfirmTitle'), {
        confirmButtonText: this.$t('CargoMsg.Cargo_Confirm'),
        cancelButtonText: this.$t('CargoMsg.Cargo_Cancel'),
        type: 'warning',
        closeOnClickModal: false
      }).then(() => {
        this.messages.splice(index, 1)
        this.$message.success(this.$t('CargoMsg.Cargo_ChatDeleteSuccess'))
      }).catch(() => {
        // 用户取消删除，不做任何操作
      })
    },
    // 处理 ADD 操作
    async processAddOperations(addRules) {
      const allAddedRules = addRules || []
      const validationResult = await this.processRuleType(allAddedRules.filter(r => r.type === 'validation'), 'validation')
      const syncResult = await this.processRuleType(allAddedRules.filter(r => r.type === 'sync'), 'sync')
      const visibilityResult = await this.processRuleType(allAddedRules.filter(r => r.type === 'visibility'), 'visibility')

      const aiMessageParts = []
      /* if (validationResult.count > 0) aiMessageParts.push(`${this.$t('CargoMsg.Cargo_AddedValidationRules')}:\n\n${validationResult.info}`)
      if (syncResult.count > 0) aiMessageParts.push(`${this.$t('CargoMsg.Cargo_AddedSyncRules')}:\n\n${syncResult.info}`)
      if (visibilityResult.count > 0) aiMessageParts.push(`${this.$t('CargoMsg.Cargo_AddedVisibilityRules')}:\n\n${visibilityResult.info}`)*/

      if (validationResult.count > 0) aiMessageParts.push(`${validationResult.info}`)
      if (syncResult.count > 0) aiMessageParts.push(`${syncResult.info}`)
      if (visibilityResult.count > 0) aiMessageParts.push(`${visibilityResult.info}`)

      const totalCount = validationResult.count + syncResult.count + visibilityResult.count
      const info = aiMessageParts.join('\n\n<hr>\n\n')

      return { count: totalCount, info }
    },

    // 处理 MODIFY 操作
    async processModifyOperations(modifyInstructions) {
      const idsToDelete = []
      const rulesToUpdate = []

      modifyInstructions.forEach(instruction => {
        // 通过键 'delete_functions_id' 来识别删除指令
        if (instruction && instruction.delete_functions_id) {
          idsToDelete.push(instruction.delete_functions_id)
        }
        // 加入更新集合
        else if (instruction && instruction.id) {
          rulesToUpdate.push(instruction)
        }
      })

      // 先删除需要修改的规则
      if (idsToDelete.length > 0) {
        idsToDelete.forEach(id => {
          functionRegistry.deleteFunctionById(this.formUuid, id)
        })
      }

      // 再添加对应规则
      const allAddedRules = rulesToUpdate || []
      const validationResult = await this.processRuleType(allAddedRules.filter(r => r.type === 'validation'), 'validation')
      const syncResult = await this.processRuleType(allAddedRules.filter(r => r.type === 'sync'), 'sync')
      const visibilityResult = await this.processRuleType(allAddedRules.filter(r => r.type === 'visibility'), 'visibility')

      const aiMessageParts = []
      /* if (validationResult.count > 0) aiMessageParts.push(`${this.$t('CargoMsg.Cargo_AddedValidationRules')}:\n\n${validationResult.info}`)
      if (syncResult.count > 0) aiMessageParts.push(`${this.$t('CargoMsg.Cargo_AddedSyncRules')}:\n\n${syncResult.info}`)
      if (visibilityResult.count > 0) aiMessageParts.push(`${this.$t('CargoMsg.Cargo_AddedVisibilityRules')}:\n\n${visibilityResult.info}`)*/

      if (validationResult.count > 0) aiMessageParts.push(`${validationResult.info}`)
      if (syncResult.count > 0) aiMessageParts.push(`${syncResult.info}`)
      if (visibilityResult.count > 0) aiMessageParts.push(`${visibilityResult.info}`)

      const totalCount = validationResult.count + syncResult.count + visibilityResult.count
      const info = aiMessageParts.join('\n\n<hr>\n\n')

      return { count: totalCount, info }
    },

    // 处理 DELETE 操作
    async processDeleteOperations(deleteRules, currentRules) {
      const ruleIdsToDelete = deleteRules || []
      if (ruleIdsToDelete.length === 0) {
        return { count: 0, info: this.$t('CargoMsg.Cargo_NoRuleDetected') }
      }

      const deletedFunctionsDetails = []

      // 将所有规则合并到一个数组中，方便通过ID查找
      const allCurrentRules = [
        ...(currentRules.validation || []),
        ...(currentRules.sync || []),
        ...(currentRules.visibility || [])
      ]

      ruleIdsToDelete.forEach(ruleId => {
        // 从合并后的规则数组中查找要删除的规则
        const ruleToDelete = allCurrentRules.find(rule => rule.id === ruleId)
        if (ruleToDelete) {
          // 复用ADD意图的显示逻辑来格式化被删除的规则信息
          const escapedCode = ruleToDelete.code.replace(/`/g, '\\`')
          let details = ''
          switch (ruleToDelete.type) {
            case 'validation': {
              const displayErrorMessage = Array.isArray(ruleToDelete.metadata.errorMessage) ? (ruleToDelete.metadata.errorMessage.find(msg => msg.language === 'zh_CN')?.name || ruleToDelete.metadata.errorMessage[0]?.name || '') : ruleToDelete.metadata.errorMessage || ''
              details = `${this.$t('CargoMsg.Cargo_ValidationRule')}: ${displayErrorMessage}\n${this.$t('CargoMsg.Cargo_RelatedElements')}: ${ruleToDelete.metadata.elements.join(', ')}`
              break
            }
            case 'sync':
              details = `${this.$t('CargoMsg.Cargo_TriggerElement')}: ${ruleToDelete.metadata.trigger.element}\n${this.$t('CargoMsg.Cargo_RelatedElements')}: ${ruleToDelete.metadata.elements.join(', ')}`
              break
            case 'visibility':
              details = `${this.$t('CargoMsg.Cargo_TargetElement')}: ${ruleToDelete.metadata.targetElement}\n${this.$t('CargoMsg.Cargo_RelatedElements')}: ${ruleToDelete.metadata.elements.join(', ')}`
              break
          }
          const fullInfo = `<div class="function-block"><div class="function-info">${this.$t('CargoMsg.Cargo_RuleID')}: ${ruleToDelete.id}\n${details}\n${this.$t('CargoMsg.Cargo_TriggerCondition')}: ${ruleToDelete.metadata.sourceQuery || ''}\n${this.$t('CargoMsg.Cargo_FunctionCode')}:\n\`\`\`javascript\n${escapedCode}\n\`\`\`</div></div>`
          deletedFunctionsDetails.push(fullInfo)
        }
        // 获取完详情后再执行删除
        functionRegistry.deleteFunctionById(this.formUuid, ruleId)
      })

      const info = deletedFunctionsDetails.join('\n\n<hr>\n\n')
      return { count: ruleIdsToDelete.length, info }
    },

    // 处理 DELETE_UNEXIST 操作
    async processDeleteUnexistOperations(deleteUnexistMessages) {
      const messages = deleteUnexistMessages || []
      if (messages.length === 0) {
        return { count: 0, info: '' }
      }

      const unexistDetails = []
      messages.forEach(message => {
        unexistDetails.push(`<div class="function-block"><div class="function-info">${message}</div></div>`)
      })

      const info = unexistDetails.join('\n\n')
      return { count: messages.length, info }
    },
    validateFormattedFunction(formattedFunction) {
      console.log('[AI聊天] 验证格式化函数', formattedFunction)
      if (!formattedFunction) {
        return false
      }

      // 验证函数、同步函数和可见性函数需要检查elements字段所有值的有效性
      const type = formattedFunction.type
      const elements = formattedFunction?.metadata?.elements
      if (!elements) {
        console.log(`[AI聊天] ${type === 'validation' ? '验证' : type === 'sync' ? '同步' : '可见性'}函数缺少elements字段`, formattedFunction)
        return false
      }
      for (const element of elements) {
        if (!this.elementMap[element]) {
          console.log(`[AI聊天] ${type === 'validation' ? '验证' : type === 'sync' ? '同步' : '可见性'}函数的元素不存在`, formattedFunction, element)
          return false
        }
      }

      // 同步函数需要检查trigger.element
      if (type === 'sync') {
        // check trigger.element
        const triggerElement = formattedFunction?.metadata?.trigger?.element
        if (!triggerElement) {
          console.log(`[AI聊天] 同步函数缺少trigger.element字段`, formattedFunction)
          return false
        }
        if (!this.elementMap[triggerElement]) {
          console.log(`[AI聊天] 同步函数的trigger.element不存在`, formattedFunction, triggerElement)
          return false
        }
      }

      // 可见性函数需要检查targetElement
      if (type === 'visibility') {
        const targetElement = formattedFunction?.metadata?.targetElement
        if (!targetElement) {
          console.log(`[AI聊天] 可见性函数缺少targetElement字段`, formattedFunction)
          return false
        }
        if (!this.elementMap[targetElement]) {
          console.log(`[AI聊天] 可见性函数的targetElement不存在`, formattedFunction, targetElement)
          return false
        }
      }
      return true
    },
    // 初始化语音识别
    async initVoiceRecognition() {
      try {
        // 检查浏览器兼容性
        const compatibility = asrUtils.checkCompatibility()
        console.log('[语音识别] 浏览器兼容性检查:', compatibility)

        if (!compatibility.mediaDevices || !compatibility.mediaRecorder || !compatibility.audioContext) {
          console.warn('[语音识别] 浏览器不支持语音识别功能')
          return
        }

        this.voiceManager = asrUtils.createManager()

        // 设置回调函数
        this.voiceManager.onError = this.handleVoiceError
        this.voiceManager.onStateChange = this.handleVoiceStateChange
        this.voiceManager.onTextUpdate = this.handleVoiceTextUpdate
        this.voiceManager.onTimeLimit = this.handleVoiceTimeLimit

        console.log('[语音识别] 初始化成功')
      } catch (error) {
        console.error('[语音识别] 初始化失败:', error)
      }
    },

    // 语音按钮点击处理（替换占位按钮逻辑）
    async handlePlaceholderAction() {
      await this.handleVoiceButtonClick()
    },

    // 语音按钮点击处理
    async handleVoiceButtonClick() {
      if (!this.voiceManager) {
        this.$message.error(this.$t('CargoMsg.Cargo_VoiceNotInitialized'))
        return
      }

      if (this.voiceRecognition.isRecording) {
        await this.stopVoiceRecognition()
      } else {
        await this.startVoiceRecognition()
      }
    },

    // 开始语音识别
    async startVoiceRecognition() {
      try {
        this.voiceRecognition.isProcessing = true
        this.voiceRecognition.error = null
        this.voiceRecognition.currentText = ''

        // 保存录音开始前的用户真实输入内容
        this.voiceRecognition.originalInputText = this.voiceRecognition.userInputText || ''
        this.voiceRecognition.accumulatedText = ''

        // 初始化时间计数器
        this.voiceRecognition.remainingTime = 60
        this.startTimeCounter()

        // 检查浏览器支持
        if (!(await this.voiceManager.isSupported())) {
          throw new Error(this.$t('CargoMsg.Cargo_VoiceBrowserNotSupported'))
        }

        // 开始录音，传入当前输入文本作为基础
        await this.voiceManager.startRecognition(this.voiceRecognition.originalInputText)

        this.voiceRecognition.isRecording = true
        this.voiceRecognition.isProcessing = false

        this.$message.success(this.$t('CargoMsg.Cargo_VoiceStartRecordingTip'))
      } catch (error) {
        this.stopTimeCounter()
        this.voiceRecognition.isProcessing = false
        this.handleVoiceError(error)
      }
    },

    // 停止语音识别
    async stopVoiceRecognition() {
      try {
        this.voiceRecognition.isProcessing = true
        this.stopTimeCounter()

        const finalText = await this.voiceManager.stopRecognition()

        this.voiceRecognition.isRecording = false
        this.voiceRecognition.isProcessing = false
        this.voiceRecognition.finalText = finalText

        // 最终文本已通过onTextUpdate回调更新到输入框，更新用户输入文本
        this.voiceRecognition.userInputText = finalText

        this.$message.success(this.$t('CargoMsg.Cargo_VoiceRecordingEnd'))
      } catch (error) {
        this.stopTimeCounter()
        this.voiceRecognition.isRecording = false
        this.voiceRecognition.isProcessing = false

        // 发生错误时恢复原始输入内容
        this.inputMessage = this.voiceRecognition.originalInputText

        this.handleVoiceError(error)
      }
    },

    // 处理文本更新（新增 - 处理重叠合并后的文本）
    handleVoiceTextUpdate(mergedText, isFinal) {
      console.log('[语音识别] 文本更新:', mergedText, '最终:', isFinal)

      // 实时更新输入框内容
      this.inputMessage = mergedText

      // 更新内部状态
      if (isFinal) {
        this.voiceRecognition.finalText = mergedText
        this.voiceRecognition.currentText = ''
      } else {
        this.voiceRecognition.currentText = mergedText
      }

      // 记录累积文本
      this.voiceRecognition.accumulatedText = mergedText
    },

    // 处理语音识别状态变化
    handleVoiceStateChange(state) {
      console.log('[语音识别] 状态变化:', state)

      // 更新本地状态
      Object.assign(this.voiceRecognition, {
        isRecording: state.isRecording,
        isProcessing: state.isProcessing,
        currentText: state.currentText,
        finalText: state.finalText,
        sessionId: state.sessionId
      })
    },

    // 处理语音识别错误
    handleVoiceError(error) {
      console.error('[语音识别] 错误:', error)

      this.voiceRecognition.error = error.message || error.userMessage || this.$t('CargoMsg.Cargo_VoiceRecognitionError')
      this.$message.error(this.voiceRecognition.error)

      // 重置状态
      this.voiceRecognition.isRecording = false
      this.voiceRecognition.isProcessing = false

      // 注意：服务器资源会在finish接口调用时自动清理，这里只需重置客户端状态
    },

    // 处理语音识别时间限制
    handleVoiceTimeLimit() {
      console.log('[语音识别] 达到60秒录音时间限制')
      this.stopTimeCounter()
      this.$message.warning(this.$t('CargoMsg.Cargo_VoiceTimeLimitReached'))
    },

    // 启动时间计数器
    startTimeCounter() {
      this.voiceRecognition.timeCounter = setInterval(() => {
        if (this.voiceRecognition.remainingTime > 0) {
          this.voiceRecognition.remainingTime--
        } else {
          this.stopTimeCounter()
        }
      }, 1000)
    },

    // 停止时间计数器
    stopTimeCounter() {
      if (this.voiceRecognition.timeCounter) {
        clearInterval(this.voiceRecognition.timeCounter)
        this.voiceRecognition.timeCounter = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.chat-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  position: relative;
  overflow: hidden;
  margin: -1px;
  padding: 0 16px;
}

.chat-messages {
  height: calc(100vh - 200px);
  overflow-y: auto;
  background-color: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 5px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  padding: 20px;
  transition: box-shadow 0.3s ease;
  position: relative;
  &:hover {
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.08);
  }
}

.loading-indicator {
  position: absolute;
  bottom: 140px; // 消息框底部
  left: 30px; // 消息框左侧
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #f4f4f5;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.loading-circle {
  width: 16px;
  height: 16px;
  border: 2px solid #409EFF;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: #606266;
  font-size: 13px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.message {
  margin-bottom: 20px;
  display: flex;
  &.user {
    justify-content: flex-end;
    .message-content {
      background-color: #ecf5ff;
      max-width: 70%;
    }
  }
  &.ai {
    justify-content: flex-start;
    .message-content {
      background-color: #f4f4f5;
      max-width: 70%;
    }
  }
}

.message-content {
  padding: 12px 16px;
  border-radius: 8px;
  display: inline-block;
  position: relative;
}

.message-header {
  margin-bottom: 4px;
  display: flex;
  justify-content: space-between; // 用户名靠左，操作按钮靠右
  align-items: center; // 垂直居中对齐
  .user-name {
    font-weight: bold;
    color: #409EFF;
    z-index: 1;
    font-size: 14px;
  }
  .message-actions {
    display: inline-flex; // 使用 inline-flex 让按钮组更紧凑
    align-items: center;
    margin-left: 8px; // 与用户名保持一定距离
    .el-button {
      padding: 0; // 移除所有内边距
      margin: 0; // 移除外边距
      border: none; // 移除边框
      &:hover {
        background: transparent; // 移除悬停背景
      }
      .el-icon {
        margin: 0; // 移除图标边距
        font-size: 14px; // 统一图标大小
      }
    }
  }
}

.message-action-btn {
  font-size: 14px;
  color: #909399;
  &:hover {
    color: #409EFF;
  }
}

.message-text {
  word-break: break-word;
  white-space: pre-wrap;
  font-size: 14px;
}

.chat-input {
  height: 180px;
  padding: 16px 0;
  background-color: #fff;
  border-top: 1px solid #e4e7ed;
  border-radius: 5px;
  box-shadow: 0 -2px 8px 0 rgba(0, 0, 0, 0.03);
  transition: box-shadow 0.3s ease;
  &:hover {
    box-shadow: 0 -4px 12px 0 rgba(0, 0, 0, 0.05);
  }

  .input-container {
    display: flex;
    gap: 12px;
    height: 100%;
    align-items: stretch;

    .message-input {
      flex: 1;
      :deep(.el-textarea__inner) {
        font-size: 14px;
        resize: none;
        height: 100% !important;
      }
    }

    .button-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 100px;

      .send-btn,
      .voice-btn {
        flex: 1;
        min-height: 36px !important;
        font-size: 13px !important;
        width: 100% !important;
        padding: 8px 12px !important;
        border-width: 1px !important;
        border-style: solid !important;
        text-align: center !important;
        box-sizing: border-box !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        margin: 0 !important;
        border-radius: 4px !important;
      }

      .send-btn {
        background-color: #409eff;
        border-color: #409eff;
        &:hover {
          background-color: #66b1ff;
          border-color: #66b1ff;
        }
      }

      .voice-btn {
        background-color: #f5f7fa;
        border-color: #dcdfe6;
        color: #606266;
        transition: all 0.3s ease;

        &:hover {
          background-color: #ecf5ff;
          border-color: #b3d8ff;
          color: #409eff;
        }

        &.recording {
          animation: pulse 2s infinite;
          box-shadow: 0 0 10px rgba(245, 108, 108, 0.5);
          background-color: #fef0f0;
          border-color: #f5c6cb;
          color: #f56565;

          &:hover {
            background-color: #fde2e2;
            border-color: #f5c6cb;
            color: #e53e3e;
          }
        }
      }
    }
  }
}

// 确认对话框样式
:deep(.message-delete-confirm) {
  .el-message-box {
    width: auto !important;
    min-width: 300px !important;
  }

  .el-message-box__btns {
    display: flex !important;
    justify-content: space-between !important;
    width: 100% !important;
    max-width: 300px !important;
    margin: 0 auto !important;
    padding: 10px 20px 20px;
  }

  .el-message-box__btns button {
    flex: 1 !important;
    margin: 0 10px !important;
    min-width: unset !important;
  }

  .el-message-box__btns button:first-child {
    margin-left: 0 !important;
  }

  .el-message-box__btns button:last-child {
    margin-right: 0 !important;
  }
}

/* 函数块样式 */
.function-block {
  margin: 16px 0;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;

  .function-info {
    margin-bottom: 12px;
    white-space: pre-wrap;
    font-family: monospace;
    line-height: 1.5;
  }
}

// 语音录音状态指示器（复用loading-indicator样式）
.voice-recording-indicator {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%) !important;
  border: 1px solid #fecaca !important;

  .voice-icon {
    color: #dc2626;
    font-size: 16px;
    animation: pulse-icon 2s infinite;
  }

  .loading-text {
    color: #dc2626 !important;
    font-weight: 500;
  }

  .voice-wave {
    display: flex;
    align-items: center;
    gap: 2px;

    .wave-bar {
      width: 3px;
      height: 12px;
      background: #dc2626;
      border-radius: 2px;
      animation: wave 1.5s infinite ease-in-out;

      &:nth-child(1) {
        animation-delay: 0s;
      }

      &:nth-child(2) {
        animation-delay: 0.2s;
      }

      &:nth-child(3) {
        animation-delay: 0.4s;
      }
    }
  }
}

// 录音状态下的输入框样式
.message-input.voice-recording {
  :deep(.el-textarea__inner) {
    background: linear-gradient(135deg, #fef2f2 0%, #fef7f7 100%);
    border-color: #fecaca;
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.1);

    &:focus {
      border-color: #f87171;
      box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
    }
  }
}

// 动画效果
@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 10px rgba(245, 108, 108, 0.5);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 15px rgba(245, 108, 108, 0.8);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 10px rgba(245, 108, 108, 0.5);
  }
}

@keyframes pulse-icon {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
}

@keyframes wave {
  0%, 100% {
    height: 8px;
    opacity: 0.4;
  }
  50% {
    height: 16px;
    opacity: 1;
  }
}
</style>
